/**
 * Phase 7.4 — Partial-completion merge behavior (pure validator + plan).
 *
 * Doctrine:
 *   - Bucket 4 §4.x    Split/merge must preserve TRUTH:
 *                       - completed work must remain completed
 *                       - incomplete work must remain visibly remaining
 *                       - completed parts must NOT be silently reopened
 *                       - incomplete parts must NOT be silently completed
 *                       - technician attribution must remain traceable
 *                       - actual time + timer history + completed_at /
 *                         stopped_at / completed_by must not be hidden
 *   - Bucket 4 §4.x    Merge must not erase Due Out / Waiter risk,
 *                       rollover/continuation lineage, or specialty-bay
 *                       context.
 *   - Bucket 4 §4.x    Manual merge must be auditable.
 *   - Bucket 4.5 §4.5.x  Phase 5.5's `validatePartialCompletionMergeDueOut`
 *                       handles the Due Out + reopen/hide blockers. This
 *                       module COMPOSES on top of it — classifying
 *                       siblings + building the recommended merge plan +
 *                       checking truth-preservation channels Phase 5.5
 *                       didn't cover (tech responsibility, actual time,
 *                       audit context, specialty bay, rollover).
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 7.4 confirmed:
 *
 *   - `src/utils/ticketMerging.ts:mergeSplitTickets` ignores sibling
 *     completion status entirely. It collects job IDs from ALL siblings
 *     (Active, Paused, Completed, running-timer alike), creates a fresh
 *     Active ticket via smart-assign, marks every sibling Deleted, and
 *     emits an OCIS `ticket_status_changed` event. There is NO check
 *     that completed work isn't silently reopened, NO check that a
 *     running-timer sibling can't be merged out from under the tech,
 *     and NO preservation of per-sibling tech attribution beyond the
 *     OCIS payload's free-text content.
 *   - `findSplitSiblings` filters `status='Deleted'` and `deleted_at` but
 *     does NOT filter `picked_up_at` or terminal states.
 *   - Phase 5.5 supplies `validatePartialCompletionMergeDueOut` requiring
 *     the caller to pre-tag children with `isCompleted: boolean`. That
 *     leaves the classification logic ambiguous — Phase 7.4 closes
 *     this gap with a canonical classifier.
 *
 * Phase 7.4 supplies the canonical completion-state classifier +
 * merge-plan + truth-preservation validator so a future preflight
 * wrapper around `mergeSplitTickets` can refuse risky merges WITHOUT
 * rewriting the merge runtime in this slice.
 *
 * Phase 7.4 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections.
 *   - No DB migration / no schema change / no RLS change.
 *   - No runtime wiring. `ticketMerging.mergeSplitTickets` is
 *     UNTOUCHED. Adoption points are documented at the bottom.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState` (terminal-state derivation).
 *   - Phase 1.4 `canPerformActiveProductionMutation` (mutation gate).
 *   - Phase 5.5 `validatePartialCompletionMergeDueOut` (Due Out gate +
 *     reopen/hide blockers).
 *   - Phase 7.1 `splitGroupIdentity` (rollover/continuation distinction).
 *   - Phase 7.3 `buildSplitPartLabelDataMap` (board-level label map;
 *     a Phase 7.4 follow-on could thread the merge-plan map alongside).
 */

import {
  getProductionState,
  canPerformActiveProductionMutation,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import {
  validatePartialCompletionMergeDueOut,
  type ValidatePartialCompletionMergeDueOutInput,
  type MergeChildSnapshot,
  type SplitMergeBlocker,
  type SplitMergeValidationResult,
} from './dueOutSplitMergeValidation';
import {
  isRolloverContinuationNotJobLineSplit,
  type SplitGroupTicketShape,
} from './splitGroupIdentity';

// =============================================================================
// Completion-state vocabulary
// =============================================================================

/**
 * Discrete completion states for a single split part. Derived from the
 * ticket row's production state + auxiliary signals (timer status,
 * `picked_up_at`, `rollover_of_ticket_id`).
 */
export type SplitPartCompletionState =
  | 'completed_part'
  | 'incomplete_part'
  | 'running_part'
  | 'paused_part'
  | 'picked_up_part'
  | 'deleted_part'
  | 'rollover_continuation_part'
  | 'unknown_part';

export const SPLIT_PART_COMPLETION_STATES = Object.freeze([
  'completed_part',
  'incomplete_part',
  'running_part',
  'paused_part',
  'picked_up_part',
  'deleted_part',
  'rollover_continuation_part',
  'unknown_part',
] as const) satisfies ReadonlyArray<SplitPartCompletionState>;

/**
 * Discrete merge-plan kinds the planner can recommend.
 *
 *   - 'merge_incomplete_remaining_work_only'      — only incomplete
 *      siblings merge; completed siblings stay tombstoned with their
 *      completion history intact and are referenced from the merged
 *      ticket's audit row.
 *   - 'preserve_completed_parts_as_completed_history' — completed
 *      siblings remain visibly preserved (status='Completed') and the
 *      merged ticket inherits their work record via summary.
 *   - 'merge_with_completed_history_summary'      — superset of the
 *      above: completed history is rolled into the merged ticket's
 *      audit payload + a separate `merge_history` JSON column.
 *   - 'block_until_manual_review'                 — at least one
 *      blocker requires human action before merge can proceed.
 *   - 'block_running_timer'                       — a running timer
 *      precludes safe merge.
 *   - 'block_terminal_or_picked_up'               — a picked-up /
 *      terminal sibling cannot be merged.
 *   - 'block_hidden_incomplete_work'              — at least one
 *      incomplete sibling would be silently completed.
 *   - 'block_completed_reopen'                    — at least one
 *      completed sibling would be silently reopened.
 *   - 'block_missing_completion_state'            — a sibling
 *      classified as `unknown_part` precludes plan derivation.
 *   - 'block_due_out_recalculation_required'      — caller did not
 *      pass a recomputed `mergedProjectedFinish` (Phase 5.5 gate).
 */
export type SplitMergePlanKind =
  | 'merge_incomplete_remaining_work_only'
  | 'preserve_completed_parts_as_completed_history'
  | 'merge_with_completed_history_summary'
  | 'block_until_manual_review'
  | 'block_running_timer'
  | 'block_terminal_or_picked_up'
  | 'block_hidden_incomplete_work'
  | 'block_completed_reopen'
  | 'block_missing_completion_state'
  | 'block_due_out_recalculation_required';

export const SPLIT_MERGE_PLAN_KINDS = Object.freeze([
  'merge_incomplete_remaining_work_only',
  'preserve_completed_parts_as_completed_history',
  'merge_with_completed_history_summary',
  'block_until_manual_review',
  'block_running_timer',
  'block_terminal_or_picked_up',
  'block_hidden_incomplete_work',
  'block_completed_reopen',
  'block_missing_completion_state',
  'block_due_out_recalculation_required',
] as const) satisfies ReadonlyArray<SplitMergePlanKind>;

/**
 * Phase 7.4 truth-preservation blockers. Extends Phase 5.5's
 * `SplitMergeBlocker` with the channels Phase 5.5 didn't cover:
 *   - tech responsibility preservation
 *   - actual-time/history preservation
 *   - job-line attribution per sibling
 *   - rollover/continuation review requirement
 *   - specialty-bay context review requirement
 *   - audit context requirement
 */
export type PartialCompletionMergeBlocker =
  | SplitMergeBlocker
  | 'missing_tech_responsibility'
  | 'missing_actual_time_history'
  | 'missing_job_line_mapping'
  | 'rollover_continuation_requires_review'
  | 'specialty_bay_context_requires_review'
  | 'audit_context_required';

// =============================================================================
// Permissive part input shape
// =============================================================================

/**
 * Permissive split-part shape. The classifier reads optional fields
 * defensively so callers can pass partial projections.
 */
export interface SplitPartShape
  extends ProductionStateTicketShape,
    SplitGroupTicketShape {
  /** Timer state column ('running' / 'paused' / 'stopped' / etc.). */
  timer_status?: string | null;
  /** Closing timestamp on the most recent timer session. */
  stopped_at?: string | Date | null;
  /** Aggregated actual-minutes from closed sessions. */
  actual_minutes?: number | null;
  /** Completion timestamp. */
  completed_at?: string | Date | null;
  /** Completion attribution. */
  completed_by?: string | null;
  completed_by_tech_id?: string | null;
  /** Pickup lifecycle. */
  picked_up_at?: string | Date | null;
  /** Specialty bay / lane signals (Phase 3.x + Phase 4.x context). */
  specialty_bay_id?: string | null;
  /** Job-line ids assigned to this part (caller-supplied). */
  jobLineIds?: ReadonlyArray<string> | null;
}

// =============================================================================
// Classifier
// =============================================================================

/**
 * Classify a single split part's completion state. Resolution order
 * (terminal first):
 *
 *   1. Rollover continuation (`rollover_of_ticket_id` set) →
 *      `rollover_continuation_part`. The continuation has its own
 *      lifecycle; merging it back into the original split group is a
 *      manual-review path (Phase 7.4 emits `rollover_continuation_requires_review`).
 *   2. `status === 'Deleted'` OR production-state `'deleted'` →
 *      `deleted_part`.
 *   3. `picked_up_at` set OR production-state `'picked_up'` →
 *      `picked_up_part`.
 *   4. `completed_at` set OR production-state `'completed_awaiting_pickup'`
 *      OR `status === 'Completed'` → `completed_part`.
 *   5. `timer_status === 'running'` → `running_part`.
 *   6. `timer_status === 'paused'` OR `status === 'Paused'` →
 *      `paused_part`.
 *   7. production state is production-relevant (scheduled / active /
 *      held / scheduled_but_parked / held_in_parking) → `incomplete_part`.
 *   8. otherwise → `unknown_part`.
 *
 * Pure.
 */
export function classifySplitPartCompletionState(
  part: SplitPartShape,
): SplitPartCompletionState {
  // 1. Rollover continuation
  if (isRolloverContinuationNotJobLineSplit(part)) {
    return 'rollover_continuation_part';
  }

  // 2. Deleted
  const status = part.status ?? null;
  const productionState: ProductionState = getProductionState(part);
  if (status === 'Deleted' || productionState === 'deleted') {
    return 'deleted_part';
  }

  // 3. Picked up
  if (part.picked_up_at != null || productionState === 'picked_up') {
    return 'picked_up_part';
  }

  // 4. Completed
  if (
    part.completed_at != null ||
    productionState === 'completed_awaiting_pickup' ||
    status === 'Completed'
  ) {
    return 'completed_part';
  }

  // 5. Running timer
  if (part.timer_status === 'running') {
    return 'running_part';
  }

  // 6. Paused
  if (part.timer_status === 'paused' || status === 'Paused') {
    return 'paused_part';
  }

  // 7. Incomplete + production-relevant
  if (canPerformActiveProductionMutation(part)) {
    return 'incomplete_part';
  }

  // 8. Defensive fallback
  return 'unknown_part';
}

/**
 * Convenience: a single part is "completed" for merge-truth purposes
 * iff its classification is `completed_part`. Picked-up parts have a
 * separate handling channel (`block_terminal_or_picked_up`).
 */
export function isCompletedSplitPart(part: SplitPartShape): boolean {
  return classifySplitPartCompletionState(part) === 'completed_part';
}

/**
 * Convenience: a single part is "incomplete and would lose visibility"
 * iff its classification is one of the incomplete-spectrum states.
 * `paused_part` IS incomplete (work pending) even though the timer is
 * stopped — the doctrine treats paused work as remaining.
 */
export function isIncompleteSplitPart(part: SplitPartShape): boolean {
  const s = classifySplitPartCompletionState(part);
  return s === 'incomplete_part' || s === 'paused_part';
}

// =============================================================================
// Tech responsibility map
// =============================================================================

/**
 * Per-part technician attribution. `assignedTechId` is the live
 * assignment; `completedByTechId` is who completed the work (if any);
 * `originalTechId` is the dispatch-plan tech (when present).
 */
export interface SplitPartTechResponsibility {
  ticketId: string;
  completionState: SplitPartCompletionState;
  assignedTechId: string | null;
  completedByTechId: string | null;
  /** Warning codes specific to this part's responsibility record. */
  warnings: PartialCompletionMergeBlocker[];
}

/**
 * Build the per-part tech responsibility map. The map preserves the
 * relationship between completion state + tech + ticket id so any
 * future merge can populate an audit row that says "tech X completed
 * jobs A,B" rather than collapsing all credit to the merged ticket's
 * primary tech.
 */
export function getSplitTechResponsibilityMap(
  parts: ReadonlyArray<SplitPartShape>,
): SplitPartTechResponsibility[] {
  const out: SplitPartTechResponsibility[] = [];
  for (const p of parts) {
    const id = String(p.id ?? '');
    if (!id) continue;
    const completionState = classifySplitPartCompletionState(p);
    const assignedTechId = (p.assigned_tech_id ?? null) as string | null;
    const completedByTechId =
      (p.completed_by_tech_id ?? null) as string | null;

    const warnings: PartialCompletionMergeBlocker[] = [];
    // Completed parts must record WHO completed them. If neither
    // attribution field is present we cannot reproduce credit.
    if (
      completionState === 'completed_part' &&
      assignedTechId == null &&
      completedByTechId == null
    ) {
      warnings.push('missing_tech_responsibility');
    }
    // Incomplete / running / paused parts should have a current
    // assignment; otherwise the merged ticket loses the dispatch plan.
    if (
      (completionState === 'incomplete_part' ||
        completionState === 'running_part' ||
        completionState === 'paused_part') &&
      assignedTechId == null
    ) {
      warnings.push('missing_tech_responsibility');
    }

    out.push({
      ticketId: id,
      completionState,
      assignedTechId,
      completedByTechId,
      warnings,
    });
  }
  return out;
}

// =============================================================================
// Completion summary (for merged ticket audit)
// =============================================================================

export interface MergedTicketCompletionSummary {
  /** Counts of each completion-state class across the group. */
  countsByState: Record<SplitPartCompletionState, number>;
  /** Ticket ids by completion state. */
  ticketIdsByState: Record<SplitPartCompletionState, string[]>;
  /** Aggregated actual minutes (sum of `actual_minutes` across all parts). */
  totalActualMinutes: number;
  /** Whether any part has `actual_minutes` set (history exists somewhere). */
  hasAnyActualMinutes: boolean;
  /** Whether any part has a `specialty_bay_id` set. */
  hasSpecialtyBayContext: boolean;
  /** Whether any part is a rollover continuation. */
  hasRolloverContinuation: boolean;
  /** Whether any part has `completed_at` set. */
  hasAnyCompletedAt: boolean;
}

function emptyStateRecord<T>(zero: T): Record<SplitPartCompletionState, T> {
  return {
    completed_part: zero,
    incomplete_part: zero,
    running_part: zero,
    paused_part: zero,
    picked_up_part: zero,
    deleted_part: zero,
    rollover_continuation_part: zero,
    unknown_part: zero,
  } as Record<SplitPartCompletionState, T>;
}

/**
 * Build a completion summary for a merged ticket's audit / history
 * payload. The summary is what the future merge mutation should
 * persist alongside the merged ticket so historical truth is
 * reproducible.
 */
export function buildMergedTicketCompletionSummary(
  parts: ReadonlyArray<SplitPartShape>,
): MergedTicketCompletionSummary {
  const counts = emptyStateRecord(0 as number);
  const ids: Record<SplitPartCompletionState, string[]> = {
    completed_part: [],
    incomplete_part: [],
    running_part: [],
    paused_part: [],
    picked_up_part: [],
    deleted_part: [],
    rollover_continuation_part: [],
    unknown_part: [],
  };
  let totalActualMinutes = 0;
  let hasAnyActualMinutes = false;
  let hasSpecialtyBayContext = false;
  let hasRolloverContinuation = false;
  let hasAnyCompletedAt = false;

  for (const p of parts) {
    const state = classifySplitPartCompletionState(p);
    counts[state] = (counts[state] ?? 0) + 1;
    if (p.id) ids[state].push(String(p.id));

    if (typeof p.actual_minutes === 'number' && Number.isFinite(p.actual_minutes)) {
      totalActualMinutes += Math.max(0, p.actual_minutes);
      hasAnyActualMinutes = true;
    }
    if (p.specialty_bay_id != null) hasSpecialtyBayContext = true;
    if (state === 'rollover_continuation_part') hasRolloverContinuation = true;
    if (p.completed_at != null) hasAnyCompletedAt = true;
  }

  return {
    countsByState: counts,
    ticketIdsByState: ids,
    totalActualMinutes,
    hasAnyActualMinutes,
    hasSpecialtyBayContext,
    hasRolloverContinuation,
    hasAnyCompletedAt,
  };
}

// =============================================================================
// Merge plan
// =============================================================================

/**
 * Caller-supplied input for plan derivation. The `auditContext` is
 * required for the plan to ever resolve to a non-block kind — Phase 7.4
 * doctrine: manual merges must be auditable.
 *
 * `mergedProjectedFinish` is forwarded to Phase 5.5's Due Out validator.
 */
export interface SplitMergeCompletionPlanInput {
  parts: ReadonlyArray<SplitPartShape>;
  /**
   * Parent (or merge-target) ticket's `due_out`. Forwarded to Phase 5.5
   * for Due Out re-evaluation. Required when any part is incomplete.
   */
  dueOut?: Date | string | null;
  /** Projected finish for the merged ticket. */
  mergedProjectedFinish?: Date | string | null;
  /** Audit context (acting user id, reason). */
  auditContext?: {
    actorUserId?: string | null;
    reason?: string | null;
  } | null;
}

export interface SplitMergeCompletionPlan {
  kind: SplitMergePlanKind;
  /** Combined blockers from Phase 5.5 + Phase 7.4 channels. */
  blockers: PartialCompletionMergeBlocker[];
  /** Per-part classification (id → state). */
  partStates: Record<string, SplitPartCompletionState>;
  /** Tech responsibility map. */
  techResponsibility: SplitPartTechResponsibility[];
  /** Completion summary for the merged ticket's audit payload. */
  completionSummary: MergedTicketCompletionSummary;
  /** Phase 5.5 Due Out validation result (when invoked). */
  dueOutValidation: SplitMergeValidationResult | null;
  /** Which sibling ids should land as completed history. */
  plannedCompletedTicketIds: string[];
  /** Which sibling ids should land as remaining (incomplete) work. */
  plannedIncompleteTicketIds: string[];
}

/**
 * Derive the recommended partial-completion merge plan for a set of
 * split parts. Composes:
 *
 *   - Per-part classification (Phase 7.4).
 *   - Tech responsibility map.
 *   - Completion summary.
 *   - Phase 5.5 `validatePartialCompletionMergeDueOut` (Due Out +
 *     reopen/hide blockers) — invoked with the derived completion
 *     buckets as `plannedCompletedChildIds` /
 *     `plannedIncompleteChildIds` so a "happy path" merge reflects
 *     truth.
 *   - Phase 7.4 truth-preservation channels.
 *
 * Pure. Does not mutate inputs.
 */
export function getSplitMergeCompletionPlan(
  input: SplitMergeCompletionPlanInput,
): SplitMergeCompletionPlan {
  const parts = input.parts ?? [];
  const partStates: Record<string, SplitPartCompletionState> = {};
  const completed: string[] = [];
  const incomplete: string[] = [];
  let anyRunning = false;
  let anyTerminal = false;
  let anyUnknown = false;
  let anyRollover = false;
  let anySpecialty = false;
  let anyCompleted = false;
  let anyIncomplete = false;
  let missingActualTimeAfterCompleted = false;
  let missingJobLineMapping = false;

  for (const p of parts) {
    const id = String(p.id ?? '');
    if (!id) continue;
    const s = classifySplitPartCompletionState(p);
    partStates[id] = s;
    if (s === 'completed_part') {
      completed.push(id);
      anyCompleted = true;
      // Phase 7.4 truth: a completed part should have actual time
      // history. If missing, flag the channel.
      if (
        (p.actual_minutes == null || !Number.isFinite(p.actual_minutes)) &&
        p.stopped_at == null
      ) {
        missingActualTimeAfterCompleted = true;
      }
    } else if (s === 'incomplete_part' || s === 'paused_part') {
      incomplete.push(id);
      anyIncomplete = true;
    } else if (s === 'running_part') {
      anyRunning = true;
      // A running part is incomplete by definition, but we hold it
      // back from `incomplete` because the merge must FIRST stop the
      // timer.
    } else if (s === 'picked_up_part' || s === 'deleted_part') {
      anyTerminal = true;
    } else if (s === 'rollover_continuation_part') {
      anyRollover = true;
    } else if (s === 'unknown_part') {
      anyUnknown = true;
    }
    if (p.specialty_bay_id != null) anySpecialty = true;
    if (p.jobLineIds != null && p.jobLineIds.length === 0) {
      // Caller supplied a mapping but it's empty — flag.
      missingJobLineMapping = true;
    }
  }

  // Phase 5.5 Due Out validation (Phase 7.4 derives the planned buckets
  // from classification so the result reflects truth).
  const children: MergeChildSnapshot[] = parts.map(p => ({
    ticketId: String(p.id ?? ''),
    ticket: p,
    hasRunningTimer: p.timer_status === 'running',
    isCompleted: classifySplitPartCompletionState(p) === 'completed_part',
  }));
  const dueOutInput: ValidatePartialCompletionMergeDueOutInput = {
    parentTicketId: '',
    dueOut: input.dueOut ?? null,
    children,
    mergedProjectedFinish: input.mergedProjectedFinish ?? null,
    plannedCompletedChildIds: completed,
    plannedIncompleteChildIds: incomplete,
  };
  const dueOutValidation = validatePartialCompletionMergeDueOut(dueOutInput);

  const blockers: PartialCompletionMergeBlocker[] = [];
  for (const b of dueOutValidation.blockers) {
    // Phase 5.5's `picked_up_or_terminal_ticket` fires for ANY terminal
    // child (including `completed_awaiting_pickup` per Phase 1.1).
    // Phase 7.4 is the authoritative classifier for this channel:
    // completed parts are an EXPECTED input to partial-completion merge
    // (the whole point of preserving completed history), so we
    // suppress Phase 5.5's blanket blocker and re-add it via Phase
    // 7.4's `anyTerminal` (picked_up + deleted only) below.
    if (b === 'picked_up_or_terminal_ticket') continue;
    if (!blockers.includes(b)) blockers.push(b);
  }
  if (anyTerminal) {
    // Phase 7.4 narrow terminal-state blocker: picked_up + deleted only.
    if (!blockers.includes('picked_up_or_terminal_ticket')) {
      blockers.push('picked_up_or_terminal_ticket');
    }
  }

  // Phase 7.4 truth-preservation channels.
  const techResponsibility = getSplitTechResponsibilityMap(parts);
  for (const r of techResponsibility) {
    for (const w of r.warnings) {
      if (!blockers.includes(w)) blockers.push(w);
    }
  }
  if (missingActualTimeAfterCompleted) {
    if (!blockers.includes('missing_actual_time_history')) {
      blockers.push('missing_actual_time_history');
    }
  }
  if (missingJobLineMapping) {
    if (!blockers.includes('missing_job_line_mapping')) {
      blockers.push('missing_job_line_mapping');
    }
  }
  if (anyRollover) {
    if (!blockers.includes('rollover_continuation_requires_review')) {
      blockers.push('rollover_continuation_requires_review');
    }
  }
  if (anySpecialty) {
    if (!blockers.includes('specialty_bay_context_requires_review')) {
      blockers.push('specialty_bay_context_requires_review');
    }
  }
  if (!input.auditContext || !input.auditContext.actorUserId) {
    if (!blockers.includes('audit_context_required')) {
      blockers.push('audit_context_required');
    }
  }

  const completionSummary = buildMergedTicketCompletionSummary(parts);

  // Plan-kind resolution (terminal first; the first matched kind wins).
  let kind: SplitMergePlanKind = 'merge_incomplete_remaining_work_only';
  if (anyUnknown) {
    kind = 'block_missing_completion_state';
  } else if (anyRunning) {
    kind = 'block_running_timer';
  } else if (anyTerminal) {
    kind = 'block_terminal_or_picked_up';
  } else if (blockers.includes('incomplete_work_would_be_hidden')) {
    kind = 'block_hidden_incomplete_work';
  } else if (blockers.includes('completed_work_would_be_reopened')) {
    kind = 'block_completed_reopen';
  } else if (blockers.includes('due_out_risk_not_recalculated')) {
    kind = 'block_due_out_recalculation_required';
  } else if (blockers.length > 0) {
    kind = 'block_until_manual_review';
  } else if (anyCompleted && anyIncomplete) {
    kind = 'merge_with_completed_history_summary';
  } else if (anyCompleted && !anyIncomplete) {
    kind = 'preserve_completed_parts_as_completed_history';
  } else {
    kind = 'merge_incomplete_remaining_work_only';
  }

  return {
    kind,
    blockers,
    partStates,
    techResponsibility,
    completionSummary,
    dueOutValidation,
    plannedCompletedTicketIds: completed,
    plannedIncompleteTicketIds: incomplete,
  };
}

// =============================================================================
// Truth-preservation validator + convenience predicate
// =============================================================================

/**
 * Validate a proposed partial-completion merge plan. Returns the same
 * shape as `SplitMergeCompletionPlan` but pinned to truth-preservation
 * semantics: every blocker is collected, the plan kind reflects the
 * worst-case blocker, and `ok` is true iff no blockers exist.
 */
export interface PartialCompletionMergeValidationResult {
  ok: boolean;
  plan: SplitMergeCompletionPlan;
}

export function validatePartialCompletionMergeTruth(
  input: SplitMergeCompletionPlanInput,
): PartialCompletionMergeValidationResult {
  const plan = getSplitMergeCompletionPlan(input);
  return { ok: plan.blockers.length === 0, plan };
}

/**
 * Convenience: collect every blocker that would fire for a given plan
 * input. Sister to `validatePartialCompletionMergeTruth` for callers
 * that want a flat blocker list without the plan envelope.
 */
export function getPartialCompletionMergeBlockers(
  input: SplitMergeCompletionPlanInput,
): PartialCompletionMergeBlocker[] {
  return getSplitMergeCompletionPlan(input).blockers;
}

/**
 * Convenience boolean: can the merge proceed without hiding any
 * completion-truth signal?
 *
 * Returns true iff every truth-preservation channel passes AND the
 * Phase 5.5 Due Out gate passes.
 */
export function canMergePartialCompletionWithoutHidingWork(
  input: SplitMergeCompletionPlanInput,
): boolean {
  return validatePartialCompletionMergeTruth(input).ok;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. mergeSplitTickets preflight:
//    `src/utils/ticketMerging.ts:mergeSplitTickets` currently merges
//    every sibling regardless of completion. Adoption: wrap the call
//    with `getSplitMergeCompletionPlan({parts: siblings, dueOut: ...,
//    mergedProjectedFinish: ..., auditContext: {actorUserId: user.id,
//    reason: 'manual_merge'}})` and refuse to invoke when
//    `plan.blockers.length > 0`. Surface the plan kind in the UI
//    confirmation modal so the user can see WHY a merge is blocked.
//
// 2. UI modal:
//    A new `MergeSplitConfirmation` component can read the plan and
//    render per-sibling completion-state badges + the blocker list +
//    the projected `completionSummary` so the user understands exactly
//    what state each part is in before confirming.
//
// 3. Audit payload:
//    The merge edge-function (or `mergeSplitTickets` itself) can write
//    `completionSummary` into a new audit_log row's `new_value`
//    payload with `AdminActionType = 'SPLIT_MERGED'`. The merged
//    ticket's history then reflects per-part completion truth.
//
// 4. Job-line preservation:
//    `mergeSplitTickets` currently re-runs smart-assign on the combined
//    job list. For Phase 7.4 plan kind
//    `merge_with_completed_history_summary`, jobs from completed
//    siblings should NOT be re-run — they should remain on the
//    deleted/completed sibling rows, and the merged ticket should
//    inherit only incomplete sibling jobs.
//
// 5. Tech responsibility:
//    The merged ticket's audit row should embed the
//    `techResponsibility` map so a future analytics query can reconstruct
//    "tech X completed jobs A,B before the merge" without parsing the
//    free-text OCIS content field.
//
// 6. Phase 5.5 integration:
//    The Phase 5.5 helper's `validatePartialCompletionMergeDueOut`
//    requires the caller to pre-classify children. Phase 7.4 closes
//    the classification gap; together they form the preflight gate.
//
// 7. Rollover continuation handling:
//    `rollover_continuation_requires_review` does NOT auto-resolve.
//    The continuation needs manual triage (likely keep it OUT of the
//    merge group and let the rollover lineage continue independently).
